import { SimUI } from '../sim_ui.js';
import { Sim } from '../sim.js';
import { EventID, TypedEvent } from '../typed_event.js';
import { wowheadSupportedLanguages } from '../constants/lang.js';
import { BooleanPicker } from '../components/boolean_picker.js';
import { EnumPicker } from '../components/enum_picker.js';
import { NumberPicker } from '../components/number_picker.js';
import { BaseModal } from './base_modal.js';
import { Tooltip } from 'bootstrap';
import { getI18n, t, type Locale } from '../i18n/index.js';

export class SettingsMenu extends BaseModal {
	private readonly simUI: SimUI;
	private tooltipInstance: Tooltip | null = null;

	constructor(parent: HTMLElement, simUI: SimUI) {
		super(parent, 'settings-menu', { title: t('settings.title'), footer: true });
		this.simUI = simUI;

		this.body.innerHTML = `
			<div class="picker-group">
				<div class="fixed-rng-seed-container">
					<div class="fixed-rng-seed"></div>
					<div class="form-text">
						<span>${t('sim.lastUsedRngSeed')}</span>&nbsp;<span class="last-used-rng-seed">0</span>
					</div>
				</div>
				<div class="language-picker within-raid-sim-hide"></div>
				<div class="ui-language-picker within-raid-sim-hide"></div>
			</div>
			<div class="show-threat-metrics-picker w-50 pe-2"></div>
			<div class="show-experimental-picker w-50 pe-2"></div>
		`;
		this.footer!.innerHTML = `
			<button
				class="restore-defaults-button btn btn-primary"
			>${t('settings.restoreDefaults')}</button>
		`

		this.restoreDefaultsButton();
		this.setupFixedRngSeed();
		this.setupLanguagePickers();
		this.setupBooleanPickers();

		// Listen for language changes and update UI
		simUI.sim.uiLocaleChangeEmitter.on(() => this.updateLanguage());
	}

	private restoreDefaultsButton() {
		const restoreDefaultsButton = this.rootElem.getElementsByClassName('restore-defaults-button')[0] as HTMLElement;
		this.tooltipInstance = Tooltip.getOrCreateInstance(restoreDefaultsButton, {
			title: t('settings.restoreDefaultsTooltip'),
		});
		restoreDefaultsButton.addEventListener('click', event => {
			this.simUI.applyDefaults(TypedEvent.nextEventID());
		});
	}

	private setupFixedRngSeed() {
		const fixedRngSeed = this.rootElem.getElementsByClassName('fixed-rng-seed')[0] as HTMLElement;
		new NumberPicker(fixedRngSeed, this.simUI.sim, {
			label: t('sim.fixedRngSeed'),
			labelTooltip: t('sim.fixedRngSeedTooltip'),
			extraCssClasses: ['mb-0'],
			changedEvent: (sim: Sim) => sim.fixedRngSeedChangeEmitter,
			getValue: (sim: Sim) => sim.getFixedRngSeed(),
			setValue: (eventID: EventID, sim: Sim, newValue: number) => {
				sim.setFixedRngSeed(eventID, newValue);
			},
		});

		const lastUsedRngSeed = this.rootElem.getElementsByClassName('last-used-rng-seed')[0] as HTMLElement;
		lastUsedRngSeed.textContent = String(this.simUI.sim.getLastUsedRngSeed());
		this.simUI.sim.lastUsedRngSeedChangeEmitter.on(() => lastUsedRngSeed.textContent = String(this.simUI.sim.getLastUsedRngSeed()));
	}

	private setupLanguagePickers() {
		const language = this.rootElem.getElementsByClassName('language-picker')[0] as HTMLElement;
		const langs = Object.keys(wowheadSupportedLanguages);
		const defaultLang = langs.indexOf('en');
		const languagePicker = new EnumPicker(language, this.simUI.sim, {
			label: t('settings.language'),
			labelTooltip: t('settings.languageTooltip'),
			values: langs.map((lang, i) => {
				return {
					name: wowheadSupportedLanguages[lang],
					value: i,
				};
			}),
			changedEvent: (sim: Sim) => sim.languageChangeEmitter,
			getValue: (sim: Sim) => {
				const idx = langs.indexOf(sim.getLanguage());
				return idx == -1 ? defaultLang : idx;
			},
			setValue: (eventID: EventID, sim: Sim, newValue: number) => {
				sim.setLanguage(eventID, langs[newValue] || 'en');
			},
		});

		const uiLanguage = this.rootElem.getElementsByClassName('ui-language-picker')[0] as HTMLElement;
		const i18n = getI18n();
		const uiLocales = i18n.getAvailableLocales();
		const defaultUiLocale = 0;
		const uiLanguagePicker = new EnumPicker(uiLanguage, this.simUI.sim, {
			label: t('settings.uiLanguage'),
			labelTooltip: t('settings.uiLanguageTooltip'),
			values: uiLocales.map((locale, i) => {
				return {
					name: i18n.getLocaleName(locale),
					value: i,
				};
			}),
			changedEvent: (sim: Sim) => sim.uiLocaleChangeEmitter,
			getValue: (sim: Sim) => {
				const idx = uiLocales.indexOf(sim.getUiLocale());
				return idx == -1 ? defaultUiLocale : idx;
			},
			setValue: (eventID: EventID, sim: Sim, newValue: number) => {
				sim.setUiLocale(eventID, uiLocales[newValue] || 'en');
			},
		});
	}

	private setupBooleanPickers() {
		const showThreatMetrics = this.rootElem.getElementsByClassName('show-threat-metrics-picker')[0] as HTMLElement;
		new BooleanPicker(showThreatMetrics, this.simUI.sim, {
			label: t('settings.showThreatMetrics'),
			labelTooltip: t('settings.showThreatMetricsTooltip'),
			inline: true,
			changedEvent: (sim: Sim) => sim.showThreatMetricsChangeEmitter,
			getValue: (sim: Sim) => sim.getShowThreatMetrics(),
			setValue: (eventID: EventID, sim: Sim, newValue: boolean) => {
				sim.setShowThreatMetrics(eventID, newValue);
			},
		});

		const showExperimental = this.rootElem.getElementsByClassName('show-experimental-picker')[0] as HTMLElement;
		new BooleanPicker(showExperimental, this.simUI.sim, {
			label: t('settings.showExperimental'),
			labelTooltip: t('settings.showExperimentalTooltip'),
			inline: true,
			changedEvent: (sim: Sim) => sim.showExperimentalChangeEmitter,
			getValue: (sim: Sim) => sim.getShowExperimental(),
			setValue: (eventID: EventID, sim: Sim, newValue: boolean) => {
				sim.setShowExperimental(eventID, newValue);
			},
		});
	}

	private updateLanguage() {
		// Update modal title
		this.setTitle(t('settings.title'));

		// Update restore defaults button
		const restoreDefaultsButton = this.rootElem.getElementsByClassName('restore-defaults-button')[0] as HTMLElement;
		restoreDefaultsButton.textContent = t('settings.restoreDefaults');
		if (this.tooltipInstance) {
			this.tooltipInstance.dispose();
		}
		this.tooltipInstance = Tooltip.getOrCreateInstance(restoreDefaultsButton, {
			title: t('settings.restoreDefaultsTooltip'),
		});

		// Update form text
		const formText = this.rootElem.querySelector('.fixed-rng-seed-container .form-text span:first-child') as HTMLElement;
		if (formText) {
			formText.textContent = t('sim.lastUsedRngSeed').replace(':', '');
		}

		// Note: The input components (NumberPicker, EnumPicker, BooleanPicker) will need
		// their own language change handlers. For now, we'll need to rebuild the entire
		// component or add update methods to those components.
		// This is a limitation that will be addressed in future iterations.
		location.reload();
	}
}
